/**
 * Meta Settings (V2) — admin page for managing direct Meta Lead Ads → ODM webhook.
 *
 * Parity with crm.wtfpoweredgyms.com/meta-settings:
 *   - Collapsible Health Dashboard (token / webhook / lead flow / forms + recommendations)
 *   - Sound on/off toggle (localStorage `wtf_lead_sound`)
 *   - Connected pages table with status/forms/leads/last-lead columns
 *   - 3-step Add Integration wizard: select page (Graph API discovered) → token → forms with gym mapping
 *   - Edit Form Mappings modal: per-form gym dropdown with live Meta + DB stats
 *
 * Mapped to: backend wtf-node-api-v2 /meta-integration/* endpoints.
 * Route: /meta-settings — parallel to legacy /meta-integration (which stays for now).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Select from "react-select";
import {
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  Globe,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle,
  Volume2,
  VolumeX,
  X,
  Pencil,
  BookOpen,
  Shield,
  Zap,
  Activity,
  Heart,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { BASE_URL } from "../../data/BaseUrl";
import { selectToken } from "../../components/auth/auth.selector";

// ─── API wrapper (self-contained) ────────────────────────────────────────────

const PREFIX = "/meta-integration";

async function callApi(token, method, path, body) {
  const res = await fetch(`${BASE_URL}${PREFIX}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status === false) {
    throw new Error(json.message || `HTTP ${res.status}`);
  }
  return json.data;
}

const metaApi = (token) => ({
  list: () => callApi(token, "GET", "/"),
  getById: (id) => callApi(token, "GET", `/${id}`),
  create: (data) => callApi(token, "POST", "/", data),
  update: (id, data) => callApi(token, "PUT", `/${id}`, data),
  toggleActive: (id) => callApi(token, "PATCH", `/${id}/toggle`),
  remove: (id) => callApi(token, "DELETE", `/${id}`),
  getPages: () => callApi(token, "GET", "/pages"),
  getForms: (pageId) => callApi(token, "GET", `/${pageId}/forms`),
  getFormStats: (id) => callApi(token, "GET", `/${id}/form-stats`),
  getHealth: () => callApi(token, "GET", "/health"),
  // New: list forms using a token passed in body (used by Add Integration wizard
  // before the page exists in our DB).
  discoverForms: (pageId, pageToken) =>
    callApi(token, "POST", "/discover-forms", { pageId, pageToken }),
});

// ─── Build the form_mappings JSON from per-form editor state ─────────────────
// Pure helper, used by both modals so the preview + the actual submit go through
// the exact same conversion path.
function buildFormMappingsJson(formStates) {
  const out = {};
  for (const [formId, s] of Object.entries(formStates || {})) {
    if (!s) continue;
    const entry = {
      formName: s.formName || formId,
      isActive: s.isActive !== false,
    };
    if (s.mode === "multi") {
      if (s.gymAliasField && s.gymAliasField.trim()) {
        entry.gymAliasField = s.gymAliasField.trim();
      }
      const map = {};
      for (const row of (s.valueMapRows || [])) {
        const lbl = (row.label || "").trim();
        const ali = (row.alias || "").trim();
        if (lbl && ali) map[lbl] = ali;
      }
      if (Object.keys(map).length) entry.gymAliasValueMap = map;
    } else {
      // single mode
      if (s.gymAlias && s.gymAlias.trim()) entry.gymAlias = s.gymAlias.trim();
    }
    if (s.preferredSlot) entry.preferredSlot = s.preferredSlot;
    if (s.leadSource) entry.leadSource = s.leadSource;
    if (s.isPt) entry.isPt = s.isPt;
    out[formId] = entry;
  }
  return out;
}

function isFormMapped(m) {
  if (!m) return false;
  if (m.gymAlias) return true;
  if (m.gymAliasField && m.gymAliasValueMap && Object.keys(m.gymAliasValueMap).length) return true;
  if (m.gymId) return true;
  return false;
}

// react-select wrapper for gym dropdowns — searchable, clearable, portals the
// menu to document.body so it isn't clipped by modal overflow.
const REACT_SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: "31px" }),
};
function GymSelect({ value, onChange, gyms, placeholder = "Select Gym", size = "sm" }) {
  const options = useMemo(
    () => (gyms || []).map((g) => ({ value: g.name, label: g.name })),
    [gyms]
  );
  const selected = options.find((o) => o.value === value) || null;
  return (
    <Select
      classNamePrefix="gym-select"
      value={selected}
      onChange={(opt) => onChange(opt ? opt.value : "")}
      options={options}
      placeholder={placeholder}
      isClearable
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
      styles={REACT_SELECT_STYLES}
    />
  );
}

async function fetchGyms(token) {
  try {
    const r = await fetch(`${BASE_URL}/gym?status=active&limit=500`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const j = await r.json().catch(() => ({}));
    const rows = j?.data ?? j?.gyms ?? j ?? [];
    if (!Array.isArray(rows)) return [];
    return rows.map((g) => ({
      id: g.user_id || g.uid || g.id,
      name: g.gym_name || g.name,
    }));
  } catch {
    return [];
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(s) {
  if (!s) return "Never";
  const d = Date.now() - new Date(s).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  return `${dd}d ago`;
}

function formCount(mappings) {
  if (!mappings || typeof mappings !== "object") return 0;
  return Object.keys(mappings).length;
}

// ─── Inline styles for hover effects + Tailwind-equivalent gaps ──────────────
const cls = {
  rowHover: {
    transition: "background-color 0.12s ease-in-out",
  },
  divider: {
    borderBottom: "1px solid #e5e7eb",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
    padding: "16px",
  },
  modalBody: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    width: "100%",
    maxWidth: "640px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  stepBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background: "#4f46e5",
    color: "#fff",
    fontSize: "11px",
    fontWeight: 600,
  },
  pillGreen: { background: "#dcfce7", color: "#15803d" },
  pillGray: { background: "#f3f4f6", color: "#6b7280" },
  pillIndigo: { background: "#eef2ff", color: "#4338ca" },
};

// ─── StatusCard (used by HealthDashboard) ────────────────────────────────────

const STATUS_CFG = {
  ok: { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0", dot: "#22c55e", label: "Healthy" },
  warning: { bg: "#fefce8", text: "#a16207", border: "#fef08a", dot: "#eab308", label: "Warning" },
  critical: { bg: "#fef2f2", text: "#b91c1c", border: "#fecaca", dot: "#ef4444", label: "Critical" },
};

function StatusCard({ icon: Icon, title, status, detail, subDetail }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.ok;
  return (
    <div
      className="p-3"
      style={{
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: "8px",
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div className="d-flex align-items-center gap-2">
          <Icon size={16} style={{ color: cfg.text }} />
          <span className="small fw-medium text-secondary">{title}</span>
        </div>
        <span
          className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill"
          style={{ background: cfg.bg, color: cfg.text, fontSize: "11px", fontWeight: 500 }}
        >
          <span
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: cfg.dot,
              display: "inline-block",
            }}
          />
          {cfg.label}
        </span>
      </div>
      <div className="fw-semibold" style={{ color: cfg.text, fontSize: "0.875rem" }}>
        {detail}
      </div>
      {subDetail && <div className="small text-muted mt-1">{subDetail}</div>}
    </div>
  );
}

// ─── HealthDashboard ─────────────────────────────────────────────────────────

function HealthDashboard({ token }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const intervalRef = useRef(null);

  const fetchHealth = useCallback(
    async (showLoading = false) => {
      try {
        if (showLoading) setLoading(true);
        setError(null);
        const data = await metaApi(token).getHealth();
        setHealth(data);
      } catch (err) {
        setError(err.message || "Failed to fetch health status");
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchHealth(true);
    intervalRef.current = setInterval(() => fetchHealth(false), 60000);
    return () => clearInterval(intervalRef.current);
  }, [fetchHealth]);

  if (loading && !health) {
    return (
      <div className="bg-white rounded shadow-sm p-4 mb-3">
        <div className="d-flex align-items-center gap-2">
          <div className="spinner-border spinner-border-sm text-secondary" role="status" />
          <span className="text-muted small">Loading integration health…</span>
        </div>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div
        className="d-flex justify-content-between align-items-center p-3 mb-3"
        style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px" }}
      >
        <div className="d-flex align-items-center gap-2 small" style={{ color: "#b91c1c" }}>
          <AlertCircle size={16} />
          <span>Health check unavailable: {error}</span>
        </div>
        <button
          onClick={() => fetchHealth(true)}
          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
        >
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  if (!health) return null;

  const overallStatus = health.overallStatus || "ok";
  const overallCfg = STATUS_CFG[overallStatus] || STATUS_CFG.ok;
  const checks = health.checks || {};
  const recommendations = health.recommendations || [];

  const t = checks.token || {};
  const w = checks.webhook || {};
  const lf = checks.leadFlow || {};
  const fs = checks.forms || {};

  return (
    <div className="bg-white rounded shadow-sm mb-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setCollapsed((p) => !p)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setCollapsed((p) => !p);
          }
        }}
        className="d-flex justify-content-between align-items-center p-3"
        style={{ cursor: "pointer", borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
      >
        <div className="d-flex align-items-center gap-2">
          <Heart size={18} style={{ color: overallCfg.text }} />
          <span className="fw-semibold">Integration Health</span>
          <span
            className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill"
            style={{
              background: overallCfg.bg,
              color: overallCfg.text,
              border: `1px solid ${overallCfg.border}`,
              fontSize: "11px",
              fontWeight: 500,
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: overallCfg.dot,
                display: "inline-block",
              }}
            />
            {overallCfg.label}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {health.checkedAt && (
            <span className="small text-muted">Checked {timeAgo(health.checkedAt)}</span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchHealth(false);
            }}
            className="btn btn-sm btn-link p-1"
            title="Refresh health"
          >
            <RefreshCw size={14} />
          </button>
          <svg
            className={collapsed ? "" : ""}
            style={{
              width: 16,
              height: 16,
              transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.15s",
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {!collapsed && (
        <div className="px-3 pb-3">
          <div className="row g-2">
            <div className="col-12 col-sm-6 col-lg-3">
              <StatusCard
                icon={Shield}
                title="Token Status"
                status={t.status || "ok"}
                detail={
                  t.status === "ok"
                    ? "Valid"
                    : t.status === "warning"
                    ? "Unknown / Env Fallback"
                    : "Invalid / Expired"
                }
                subDetail={
                  t.lastChecked
                    ? `Checked ${timeAgo(t.lastChecked)}`
                    : t.invalid
                    ? `${t.invalid} invalid`
                    : null
                }
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <StatusCard
                icon={Zap}
                title="Webhook"
                status={w.status || "ok"}
                detail={w.status === "ok" ? "Active" : w.status === "warning" ? "Slow" : "Stale"}
                subDetail={
                  w.lastWebhookAt
                    ? `Last ${timeAgo(w.lastWebhookAt)}`
                    : "No webhooks received"
                }
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <StatusCard
                icon={Activity}
                title="Lead Flow"
                status={lf.status || "ok"}
                detail={lf.status === "ok" ? "Flowing" : lf.status === "warning" ? "Slow" : "Stale"}
                subDetail={
                  lf.lastLeadAt
                    ? `Last lead ${timeAgo(lf.lastLeadAt)}${
                        lf.hoursSince !== null && lf.hoursSince !== undefined
                          ? ` (${lf.hoursSince}h ago)`
                          : ""
                      }`
                    : "No leads received"
                }
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <StatusCard
                icon={FileText}
                title="Forms"
                status={fs.status || "ok"}
                detail={`${fs.active || 0} active of ${fs.total || 0} total`}
                subDetail={
                  fs.unmapped > 0
                    ? `${fs.unmapped} unmapped`
                    : fs.total > 0
                    ? "All forms mapped"
                    : "No forms configured"
                }
              />
            </div>
          </div>

          {recommendations.length > 0 && (
            <div
              className="mt-3 p-3"
              style={{
                background: "#fefce8",
                border: "1px solid #fef08a",
                borderRadius: "8px",
              }}
            >
              <div
                className="small fw-semibold d-flex align-items-center gap-1 mb-2"
                style={{ color: "#a16207" }}
              >
                <AlertCircle size={14} />
                Recommendations
              </div>
              <ul className="mb-0 small" style={{ color: "#a16207", paddingLeft: "1.1rem" }}>
                {recommendations.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── IntegrationRow ──────────────────────────────────────────────────────────

function IntegrationRow({ integration, onToggle, onDelete, onEditForms }) {
  const count = formCount(integration.form_mappings);
  const mappings = integration.form_mappings || {};
  const mapped = Object.values(mappings).filter(isFormMapped).length;

  return (
    <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
      <td className="p-3">
        <div className="d-flex align-items-center gap-2">
          <Globe size={16} style={{ color: "#3b82f6", flexShrink: 0 }} />
          <span className="fw-medium">{integration.page_name}</span>
        </div>
        <p className="small text-muted mt-1 mb-0" style={{ marginLeft: "24px" }}>
          {integration.page_id}
        </p>
      </td>
      <td className="p-3">
        {integration.is_active ? (
          <span
            className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill"
            style={{ ...cls.pillGreen, fontSize: "11px", fontWeight: 500 }}
          >
            <CheckCircle size={12} />
            Connected
          </span>
        ) : (
          <span
            className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill"
            style={{ ...cls.pillGray, fontSize: "11px", fontWeight: 500 }}
          >
            <AlertCircle size={12} />
            Inactive
          </span>
        )}
      </td>
      <td className="p-3">
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center gap-1 small text-muted">
            <FileText size={14} style={{ color: "#9ca3af" }} />
            {count > 0 ? (
              <span>
                {count} form{count !== 1 ? "s" : ""}
                {mapped === 0 ? (
                  <span className="ms-1" style={{ color: "#d97706", fontSize: "11px" }}>
                    (no gyms mapped)
                  </span>
                ) : mapped < count ? (
                  <span className="ms-1" style={{ color: "#d97706", fontSize: "11px" }}>
                    ({mapped}/{count} mapped)
                  </span>
                ) : (
                  <span className="ms-1 text-success" style={{ fontSize: "11px" }}>
                    (all mapped)
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted">No forms</span>
            )}
          </div>
          {count > 0 && (
            <button
              onClick={() => onEditForms(integration)}
              className="btn btn-sm btn-link p-1"
              title="Edit form-gym mappings"
              style={{ color: "#6366f1" }}
            >
              <Pencil size={14} />
            </button>
          )}
        </div>
      </td>
      <td className="p-3 fw-medium">{integration.lead_count || 0}</td>
      <td className="p-3">
        <div className="d-flex align-items-center gap-1 small text-muted">
          <Clock size={14} style={{ color: "#9ca3af" }} />
          {timeAgo(integration.last_lead_at)}
        </div>
      </td>
      <td className="p-3">
        <div className="d-flex align-items-center gap-2">
          <button
            onClick={() => onToggle(integration.id)}
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            title={integration.is_active ? "Deactivate" : "Activate"}
          >
            {integration.is_active ? (
              <ToggleRight size={16} style={{ color: "#22c55e" }} />
            ) : (
              <ToggleLeft size={16} style={{ color: "#9ca3af" }} />
            )}
            <span>{integration.is_active ? "Active" : "Inactive"}</span>
          </button>
          <button
            onClick={() => onDelete(integration.id)}
            className="btn btn-sm btn-link p-1"
            title="Delete integration"
            style={{ color: "#dc2626" }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── AddIntegrationModal ─────────────────────────────────────────────────────

function AddIntegrationModal({
  onClose,
  onSubmit,
  availablePages,
  availableForms,
  selectedPage,
  loadingPages,
  loadingForms,
  onSelectPage,
  gyms,
}) {
  const [tokenInput, setTokenInput] = useState("");
  const [selectedFormIds, setSelectedFormIds] = useState([]);
  const [formGymMap, setFormGymMap] = useState({});
  // confirm: false = editing, true = preview-and-confirm step
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleForm = (formId) =>
    setSelectedFormIds((prev) =>
      prev.includes(formId) ? prev.filter((id) => id !== formId) : [...prev, formId]
    );

  // Build the JSON body we'll POST. Same object is used for preview + actual submit.
  const submitPayload = useMemo(() => {
    if (!selectedPage) return null;
    const formMappings = {};
    for (const formId of selectedFormIds) {
      const form = availableForms.find((f) => f.id === formId);
      const mapping = { formName: form?.name || formId, isActive: true };
      if (formGymMap[formId]) mapping.gymAlias = formGymMap[formId];
      formMappings[formId] = mapping;
    }
    return {
      page_id: selectedPage.id,
      page_name: selectedPage.name,
      access_token: tokenInput ? `<encrypted: ${tokenInput.length} chars>` : null, // preview-safe
      _tokenRaw: tokenInput.trim(), // private — used by handleConfirm, NOT shown
      form_mappings: formMappings,
    };
  }, [selectedPage, selectedFormIds, formGymMap, availableForms, tokenInput]);

  const handleOpenPreview = () => {
    if (!selectedPage) { toast.error("Please select a page first"); return; }
    if (!tokenInput.trim()) { toast.error("Page access token is required"); return; }
    setConfirming(true);
  };

  const handleConfirm = () => {
    const { _tokenRaw, ...rest } = submitPayload;
    onSubmit({ ...rest, access_token: _tokenRaw });
  };

  return (
    <div style={cls.modalBackdrop} role="dialog" aria-modal="true">
      <div style={cls.modalBody}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center p-3" style={cls.divider}>
          <div>
            <h5 className="mb-0">Add Meta Page Integration</h5>
            <div className="small text-muted">
              Connect a Facebook/Instagram page to receive leads
            </div>
          </div>
          <button onClick={onClose} className="btn btn-sm btn-link text-muted">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-3 overflow-auto" style={{ flex: 1 }}>
          {/* Step 1 */}
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 fw-semibold small mb-2">
              <span style={cls.stepBadge}>1</span>
              Select Facebook Page
            </div>
            {loadingPages ? (
              <div className="d-flex align-items-center gap-2 small text-muted py-2">
                <RefreshCw size={16} className="spinning" />
                Discovering pages from Meta Graph API…
              </div>
            ) : availablePages.length === 0 ? (
              <div
                className="d-flex align-items-center gap-2 small p-3"
                style={{
                  background: "#fefce8",
                  border: "1px solid #fef08a",
                  borderRadius: "8px",
                  color: "#a16207",
                }}
              >
                <AlertCircle size={16} />
                No pages found. Ensure META_SYSTEM_USER_TOKEN is set on the backend.
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {availablePages.map((page) => {
                  const sel = selectedPage?.id === page.id;
                  return (
                    <label
                      key={page.id}
                      className="d-flex align-items-center gap-2 p-3 rounded"
                      style={{
                        border: `1px solid ${sel ? "#6366f1" : "#e5e7eb"}`,
                        background: sel ? "#eef2ff" : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="selectedPage"
                        value={page.id}
                        checked={sel}
                        onChange={() => onSelectPage(page)}
                      />
                      <Globe size={16} style={{ color: "#3b82f6", flexShrink: 0 }} />
                      <div>
                        <div className="small fw-medium">{page.name}</div>
                        <div className="small text-muted">{page.id}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 2 */}
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 fw-semibold small mb-2">
              <span style={cls.stepBadge}>2</span>
              Page Access Token <span className="text-danger">*</span>
            </div>
            <input
              type="text"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="EAAxxxxx... (long-lived page access token)"
              className="form-control"
            />
            <div className="small text-muted mt-1">
              Generate a long-lived page access token from Meta Business Suite or Graph API
              Explorer.
            </div>
          </div>

          {/* Step 3 — optional form selection with per-form gym dropdown */}
          {selectedPage && (
            <div>
              <div className="d-flex align-items-center gap-2 fw-semibold small mb-2">
                <span style={cls.stepBadge}>3</span>
                Select Lead Forms (optional)
              </div>
              {loadingForms ? (
                <div className="d-flex align-items-center gap-2 small text-muted py-2">
                  <RefreshCw size={16} className="spinning" />
                  Loading forms for {selectedPage.name}…
                </div>
              ) : availableForms.length === 0 ? (
                <div className="small text-muted p-3 bg-light rounded">
                  No lead forms found for this page. You can add form mappings later.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {availableForms.map((form) => {
                    const sel = selectedFormIds.includes(form.id);
                    return (
                      <div
                        key={form.id}
                        className="p-3 rounded"
                        style={{
                          border: `1px solid ${sel ? "#6366f1" : "#e5e7eb"}`,
                          background: sel ? "#eef2ff" : "transparent",
                        }}
                      >
                        <label
                          className="d-flex align-items-center gap-2"
                          style={{ cursor: "pointer", marginBottom: 0 }}
                        >
                          <input
                            type="checkbox"
                            checked={sel}
                            onChange={() => toggleForm(form.id)}
                          />
                          <FileText size={16} style={{ color: "#9ca3af", flexShrink: 0 }} />
                          <div className="flex-grow-1">
                            <div className="small fw-medium">{form.name}</div>
                            <div className="small text-muted">{form.id}</div>
                          </div>
                        </label>
                        {sel && (
                          <div className="d-flex align-items-center gap-2 mt-2 ms-4">
                            <BookOpen size={14} style={{ color: "#9ca3af" }} />
                            <div style={{ flex: 1 }}>
                              <GymSelect
                                value={formGymMap[form.id] || ""}
                                onChange={(val) =>
                                  setFormGymMap((p) => ({ ...p, [form.id]: val }))
                                }
                                gyms={gyms}
                                placeholder="Select Gym (recommended)"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="d-flex justify-content-end align-items-center gap-2 p-3"
          style={{ borderTop: "1px solid #e5e7eb", background: "#f9fafb", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}
        >
          <button onClick={onClose} className="btn btn-link text-muted">
            Cancel
          </button>
          <button
            onClick={handleOpenPreview}
            disabled={!selectedPage || !tokenInput.trim()}
            className="btn btn-primary"
          >
            Preview & Add
          </button>
        </div>
      </div>

      {confirming && (
        <PreviewConfirmDialog
          title="Confirm New Integration"
          subtitle={`Page: ${selectedPage?.name} (${selectedPage?.id})`}
          payload={(() => { const { _tokenRaw, ...rest } = submitPayload; return rest; })()}
          confirmLabel="Add Integration"
          onBack={() => setConfirming(false)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}

// ─── PreviewConfirmDialog ────────────────────────────────────────────────────
// Renders the exact JSON about to be submitted with Back/Confirm.
// Stacks ON TOP of an existing modal (uses z-index 1100).

function PreviewConfirmDialog({ title, subtitle, payload, confirmLabel, onBack, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const json = JSON.stringify(payload, null, 2);
  const onConfirmClick = async () => {
    setSubmitting(true);
    try { await onConfirm(); } finally { setSubmitting(false); }
  };
  return (
    <div style={{ ...cls.modalBackdrop, zIndex: 1100 }} role="dialog" aria-modal="true">
      <div style={{ ...cls.modalBody, maxWidth: "720px" }}>
        <div className="d-flex justify-content-between align-items-center p-3" style={cls.divider}>
          <div>
            <h5 className="mb-0">{title}</h5>
            {subtitle && <div className="small text-muted">{subtitle}</div>}
          </div>
          <button onClick={onBack} className="btn btn-sm btn-link text-muted" disabled={submitting}>
            <X size={20} />
          </button>
        </div>
        <div className="p-3 overflow-auto" style={{ flex: 1 }}>
          <div className="small text-muted mb-2">
            Review the JSON below. This is exactly what will be saved to the backend.
            Click <strong>Back to Edit</strong> to make further changes.
          </div>
          <pre
            className="p-3 mb-0"
            style={{
              background: "#0f172a",
              color: "#e2e8f0",
              borderRadius: "8px",
              fontSize: "12.5px",
              lineHeight: "1.6",
              maxHeight: "55vh",
              overflow: "auto",
              margin: 0,
            }}
          >{json}</pre>
        </div>
        <div
          className="d-flex justify-content-end align-items-center gap-2 p-3"
          style={{ borderTop: "1px solid #e5e7eb", background: "#f9fafb", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}
        >
          <button onClick={onBack} className="btn btn-link text-muted" disabled={submitting}>
            Back to Edit
          </button>
          <button onClick={onConfirmClick} disabled={submitting} className="btn btn-primary">
            {submitting ? "Submitting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EditFormMappingsModal ────────────────────────────────────────────────────

function EditFormMappingsModal({ integration, gyms, token, onClose, onSave }) {
  const [forms, setForms] = useState([]);
  // Per-form editor state, keyed by formId. Each entry holds the mode +
  // either gymAlias (single) or gymAliasField/valueMapRows (multi), plus
  // misc passthrough fields.
  const [formStates, setFormStates] = useState({});
  // Expanded/collapsed accordion state — formId → bool. Default: all collapsed.
  const [expandedForms, setExpandedForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const toggleExpanded = (formId) =>
    setExpandedForms((p) => ({ ...p, [formId]: !p[formId] }));
  const expandAll = () =>
    setExpandedForms(forms.reduce((acc, f) => ({ ...acc, [f.formId]: true }), {}));
  const collapseAll = () => setExpandedForms({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await metaApi(token).getFormStats(integration.id);
        if (cancelled) return;
        const list = data?.forms || [];
        setForms(list);
        // Seed editor state from server data — pick mode based on what's set
        const initial = {};
        for (const f of list) {
          const hasMulti = !!(
            f.gymAliasField ||
            (f.gymAliasValueMap && Object.keys(f.gymAliasValueMap).length)
          );
          const valueMapRows = f.gymAliasValueMap
            ? Object.entries(f.gymAliasValueMap).map(([label, alias]) => ({ label, alias }))
            : [];
          initial[f.formId] = {
            formName: f.formName || f.formId,
            isActive: f.isActive !== false,
            mode: hasMulti ? "multi" : "single",
            gymAlias: f.gymAlias || "",
            gymAliasField: f.gymAliasField || "",
            valueMapRows: valueMapRows.length ? valueMapRows : [{ label: "", alias: "" }],
            isPt: f.isPt || 0,
            preferredSlot: f.preferredSlot || "",
            leadSource: f.leadSource || "",
          };
        }
        setFormStates(initial);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load form data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [integration.id, token]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const updateForm = (formId, patch) =>
    setFormStates((prev) => ({ ...prev, [formId]: { ...prev[formId], ...patch } }));

  const updateValueMapRow = (formId, idx, patch) =>
    setFormStates((prev) => {
      const cur = prev[formId];
      if (!cur) return prev;
      const rows = cur.valueMapRows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
      return { ...prev, [formId]: { ...cur, valueMapRows: rows } };
    });

  const addValueMapRow = (formId) =>
    setFormStates((prev) => {
      const cur = prev[formId];
      if (!cur) return prev;
      return { ...prev, [formId]: { ...cur, valueMapRows: [...cur.valueMapRows, { label: "", alias: "" }] } };
    });

  const removeValueMapRow = (formId, idx) =>
    setFormStates((prev) => {
      const cur = prev[formId];
      if (!cur) return prev;
      const rows = cur.valueMapRows.filter((_, i) => i !== idx);
      return { ...prev, [formId]: { ...cur, valueMapRows: rows.length ? rows : [{ label: "", alias: "" }] } };
    });

  // Form mappings JSON we'd submit — used by preview + actual save
  const formMappings = useMemo(() => buildFormMappingsJson(formStates), [formStates]);

  const handleOpenPreview = () => setConfirming(true);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onSave(integration.id, { form_mappings: formMappings });
      toast.success("Form-gym mappings updated");
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to update mappings");
    } finally {
      setSaving(false);
    }
  };

  const mappedCount = Object.values(formMappings).filter(isFormMapped).length;
  const totalLeads = forms.reduce((s, f) => s + (f.wtfLeadCount || 0), 0);

  return (
    <div style={cls.modalBackdrop} role="dialog" aria-modal="true">
      <div style={{ ...cls.modalBody, maxWidth: "740px" }}>
        <div className="d-flex justify-content-between align-items-center p-3" style={cls.divider}>
          <div>
            <h5 className="mb-0">Edit Form → Gym Mapping</h5>
            <div className="small text-muted">
              {integration.page_name} — {mappedCount}/{forms.length} mapped · {totalLeads} total leads
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {forms.length > 0 && (
              <div className="btn-group btn-group-sm" role="group" aria-label="Expand controls">
                <button
                  type="button"
                  onClick={expandAll}
                  className="btn btn-outline-secondary"
                  style={{ fontSize: "11px" }}
                  title="Expand all forms"
                >Expand all</button>
                <button
                  type="button"
                  onClick={collapseAll}
                  className="btn btn-outline-secondary"
                  style={{ fontSize: "11px" }}
                  title="Collapse all forms"
                >Collapse all</button>
              </div>
            )}
            <button onClick={onClose} className="btn btn-sm btn-link text-muted">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-3 overflow-auto" style={{ flex: 1 }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center gap-2 py-4 small text-muted">
              <RefreshCw size={16} className="spinning" />
              Loading forms from Meta & lead stats…
            </div>
          ) : error ? (
            <div className="d-flex align-items-center gap-2 small p-3"
              style={{ background: "#fef2f2", color: "#b91c1c", borderRadius: "8px" }}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : forms.length === 0 ? (
            <div className="small text-muted text-center py-3">No forms found for this integration.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {forms.map((f) => {
                const s = formStates[f.formId] || {};
                const isExpanded = !!expandedForms[f.formId];
                // Quick "mapped?" check for the header badge so users can see at-a-glance
                // which forms still need attention without expanding each one.
                const previewMapping = buildFormMappingsJson({ [f.formId]: s })[f.formId];
                const rowMapped = isFormMapped(previewMapping);
                return (
                  <div key={f.formId} className="rounded" style={{ border: "1px solid #e5e7eb" }}>
                    {/* Clickable collapsed header */}
                    <div
                      onClick={() => toggleExpanded(f.formId)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleExpanded(f.formId); } }}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      className="d-flex justify-content-between align-items-center gap-2 p-3"
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                        {isExpanded ? (
                          <ChevronDown size={16} style={{ color: "#6b7280", flexShrink: 0 }} />
                        ) : (
                          <ChevronRight size={16} style={{ color: "#6b7280", flexShrink: 0 }} />
                        )}
                        <FileText size={14} style={{ color: "#9ca3af", flexShrink: 0 }} />
                        <div style={{ minWidth: 0, overflow: "hidden" }}>
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="small fw-medium text-truncate"
                              style={{ display: "inline-block", maxWidth: "100%" }}
                            >
                              {f.formName || f.formId}
                            </span>
                            {f.metaStatus === "ACTIVE" && (
                              <span className="px-2 py-1 rounded-pill"
                                style={{ ...cls.pillGreen, fontSize: "10px", fontWeight: 500, flexShrink: 0 }}>ACTIVE</span>
                            )}
                            {rowMapped ? (
                              <span className="px-2 py-1 rounded-pill"
                                style={{ ...cls.pillGreen, fontSize: "10px", fontWeight: 500, flexShrink: 0 }}>
                                {s.mode === "multi"
                                  ? `${(s.valueMapRows || []).filter(r => r.label && r.alias).length} answers`
                                  : "mapped"}
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-pill"
                                style={{ background: "#fef3c7", color: "#92400e", fontSize: "10px", fontWeight: 500, flexShrink: 0 }}>
                                not mapped
                              </span>
                            )}
                          </div>
                          <div className="small text-muted">
                            {f.formId} · Meta: {f.metaLeadsCount || 0} · WTF: {f.wtfLeadCount || 0}
                            {f.lastLeadAt && ` · Last ${timeAgo(f.lastLeadAt)}`}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded body */}
                    {isExpanded && (
                      <div className="p-3" style={{ borderTop: "1px solid #f3f4f6" }}>
                        {/* Mode toggle */}
                        <div className="d-flex justify-content-end mb-3">
                          <div className="btn-group btn-group-sm" role="group" aria-label="Mapping mode">
                            <button
                              type="button"
                              onClick={() => updateForm(f.formId, { mode: "single" })}
                              className={`btn ${s.mode === "single" ? "btn-primary" : "btn-outline-secondary"}`}
                              style={{ fontSize: "11px" }}
                            >Single gym</button>
                            <button
                              type="button"
                              onClick={() => updateForm(f.formId, { mode: "multi" })}
                              className={`btn ${s.mode === "multi" ? "btn-primary" : "btn-outline-secondary"}`}
                              style={{ fontSize: "11px" }}
                            >Multi-gym dropdown</button>
                          </div>
                        </div>

                    {/* Single-gym editor */}
                    {s.mode === "single" && (
                      <div className="d-flex align-items-center gap-2 mt-3">
                        <BookOpen size={14} style={{ color: "#9ca3af" }} />
                        <div style={{ flex: 1 }}>
                          <GymSelect
                            value={s.gymAlias || ""}
                            onChange={(val) => updateForm(f.formId, { gymAlias: val })}
                            gyms={gyms}
                          />
                        </div>
                      </div>
                    )}

                    {/* Multi-gym editor */}
                    {s.mode === "multi" && (
                      <div className="mt-3">
                        <div className="small fw-medium mb-1">Meta dropdown field name</div>
                        <input
                          type="text"
                          value={s.gymAliasField || ""}
                          onChange={(e) => updateForm(f.formId, { gymAliasField: e.target.value })}
                          placeholder='e.g. "preferred_gym" or the exact Meta field.name'
                          className="form-control form-control-sm mb-2"
                        />
                        <div className="small text-muted mb-2">
                          The internal <code>field_data.name</code> Meta sends for the gym-selection
                          question. Find it via Graph API: <code>GET /{`<form_id>`}?fields=questions</code>.
                        </div>

                        <div className="small fw-medium mb-1">Map dropdown answers → gym</div>
                        <div className="d-flex flex-column gap-1">
                          {(s.valueMapRows || []).map((row, idx) => (
                            <div key={idx} className="d-flex align-items-center gap-2">
                              <input
                                type="text"
                                value={row.label}
                                onChange={(e) => updateValueMapRow(f.formId, idx, { label: e.target.value })}
                                placeholder="Meta dropdown label (e.g. Sector 116, Noida)"
                                className="form-control form-control-sm"
                                style={{ flex: 1 }}
                              />
                              <span className="text-muted">→</span>
                              <div style={{ flex: 1 }}>
                                <GymSelect
                                  value={row.alias}
                                  onChange={(val) => updateValueMapRow(f.formId, idx, { alias: val })}
                                  gyms={gyms}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeValueMapRow(f.formId, idx)}
                                className="btn btn-sm btn-link text-danger p-1"
                                title="Remove row"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => addValueMapRow(f.formId)}
                          className="btn btn-sm btn-link p-0 mt-1 d-inline-flex align-items-center gap-1"
                        >
                          <Plus size={14} /> Add answer
                        </button>
                      </div>
                    )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          className="d-flex justify-content-between align-items-center gap-2 p-3"
          style={{ borderTop: "1px solid #e5e7eb", background: "#f9fafb", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}
        >
          <div className="small text-muted">
            Click <strong>Preview & Save</strong> to see the JSON before submitting.
          </div>
          <div className="d-flex gap-2">
            <button onClick={onClose} className="btn btn-link text-muted" disabled={saving}>
              Cancel
            </button>
            <button onClick={handleOpenPreview} disabled={loading || saving} className="btn btn-primary">
              Preview & Save
            </button>
          </div>
        </div>
      </div>

      {confirming && (
        <PreviewConfirmDialog
          title="Confirm Form-Gym Mappings"
          subtitle={`${integration.page_name} — ${mappedCount}/${forms.length} mapped`}
          payload={{ form_mappings: formMappings }}
          confirmLabel="Save Mappings"
          onBack={() => setConfirming(false)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function MetaSettings() {
  const token = useSelector(selectToken);
  // useMemo so `api` identity is stable across renders — without this every
  // render creates a new `api` object, which invalidates refreshIntegrations'
  // useCallback identity, which re-fires the mount useEffect, causing an
  // infinite fetch loop.
  const api = useMemo(() => metaApi(token), [token]);

  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [availableForms, setAvailableForms] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [gyms, setGyms] = useState([]);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(
    typeof window !== "undefined" && localStorage.getItem("wtf_lead_sound") !== "off"
  );

  const refreshIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      setIntegrations(await api.list());
    } catch (err) {
      toast.error(err.message || "Failed to load integrations");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    refreshIntegrations();
    fetchGyms(token).then(setGyms);
  }, [refreshIntegrations, token]);

  const handleToggle = async (id) => {
    try {
      await api.toggleActive(id);
      await refreshIntegrations();
      toast.success("Integration toggled");
    } catch (err) {
      toast.error(err.message || "Failed to toggle");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this integration? This cannot be undone.")) return;
    try {
      await api.remove(id);
      await refreshIntegrations();
      toast.success("Integration deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    }
  };

  const handleDiscoverPages = async () => {
    setLoadingPages(true);
    setAvailablePages([]);
    setAvailableForms([]);
    setSelectedPage(null);
    try {
      const data = await api.getPages();
      setAvailablePages(data || []);
    } catch (err) {
      toast.error(err.message || "Failed to discover pages.");
    } finally {
      setLoadingPages(false);
    }
  };

  const handleSelectPage = async (page) => {
    setSelectedPage(page);
    setAvailableForms([]);
    setLoadingForms(true);
    try {
      // Prefer the page's access_token returned by /me/accounts (works even
      // when the page isn't yet stored as an integration). Fall back to the
      // stored-token endpoint if discovery didn't yield a token.
      const data = page.access_token
        ? await api.discoverForms(page.id, page.access_token)
        : await api.getForms(page.id);
      setAvailableForms(data || []);
    } catch {
      setAvailableForms([]);
    } finally {
      setLoadingForms(false);
    }
  };

  const handleAddIntegration = async (data) => {
    try {
      await api.create(data);
      setShowAddModal(false);
      await refreshIntegrations();
      toast.success("Integration added");
    } catch (err) {
      toast.error(err.message || "Failed to add integration");
    }
  };

  const handleUpdateIntegration = async (id, data) => {
    await api.update(id, data);
    await refreshIntegrations();
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem("wtf_lead_sound", next ? "on" : "off");
  };

  const onOpenAdd = () => {
    setShowAddModal(true);
    handleDiscoverPages();
  };

  return (
    <div className="container-fluid py-3">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .spinning{animation:spin 0.9s linear infinite}`}</style>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-0">Meta Lead Settings</h3>
          <div className="text-muted small">
            Manage Facebook Lead Ads integration · direct Meta → ODM webhook (replaces make.com)
          </div>
        </div>
        <div className="d-flex gap-2">
          <button
            onClick={toggleSound}
            className="btn btn-outline-secondary d-flex align-items-center gap-1"
            title={soundEnabled ? "Sound on — click to mute" : "Sound off — click to enable"}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            <span className="small">{soundEnabled ? "Sound On" : "Sound Off"}</span>
          </button>
          <button onClick={onOpenAdd} className="btn btn-primary d-flex align-items-center gap-1">
            <Plus size={16} /> Add Page
          </button>
        </div>
      </div>

      <HealthDashboard token={token} />

      <div className="bg-white rounded shadow-sm">
        <div
          className="d-flex justify-content-between align-items-center p-3"
          style={cls.divider}
        >
          <h6 className="mb-0">Connected Pages</h6>
          <div className="small text-muted">
            {integrations.length} integration{integrations.length !== 1 ? "s" : ""}
          </div>
        </div>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-secondary" role="status" />
          </div>
        ) : integrations.length === 0 ? (
          <div className="p-4 text-center">
            <div className="text-muted small mb-3">
              No Meta pages connected yet. Click <strong>Add Page</strong> to set up the first one.
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table mb-0">
              <thead>
                <tr style={{ background: "#f9fafb", color: "#6b7280" }}>
                  <th className="p-3 small fw-semibold">Page</th>
                  <th className="p-3 small fw-semibold">Status</th>
                  <th className="p-3 small fw-semibold">Forms</th>
                  <th className="p-3 small fw-semibold">Leads</th>
                  <th className="p-3 small fw-semibold">Last Lead</th>
                  <th className="p-3 small fw-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {integrations.map((it) => (
                  <IntegrationRow
                    key={it.id}
                    integration={it}
                    onToggle={handleToggle}
                    onDelete={handleDelete}
                    onEditForms={setEditingIntegration}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <AddIntegrationModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddIntegration}
          availablePages={availablePages}
          availableForms={availableForms}
          selectedPage={selectedPage}
          loadingPages={loadingPages}
          loadingForms={loadingForms}
          onSelectPage={handleSelectPage}
          gyms={gyms}
        />
      )}

      {editingIntegration && (
        <EditFormMappingsModal
          integration={editingIntegration}
          gyms={gyms}
          token={token}
          onClose={() => setEditingIntegration(null)}
          onSave={handleUpdateIntegration}
        />
      )}
    </div>
  );
}
